<template>
    <Handle type="target" :position="Position.Left" id="input" />
    <NodeBase title="Set Property" icon="✏" color="#7c3aed">
        <label>Page</label>
        <select :value="props.data.pageId" @change="onPageChange($event.target.value)">
            <option value="">-- page --</option>
            <option v-for="p in pages" :key="p.id" :value="p.id">{{ p.label }}</option>
        </select>

        <label>Object</label>
        <select :value="props.data.objectId" @change="onObjectChange($event.target.value)">
            <option value="">-- object --</option>
            <option v-for="o in objects" :key="o.id" :value="o.id">{{ o.label }}</option>
        </select>

        <label>Property</label>
        <select :value="props.data.property" @change="onPropChange($event.target.value)">
            <option value="">-- property --</option>
            <option v-for="p in properties" :key="p.name" :value="p.name">{{ p.name }}</option>
        </select>

        <label>Value</label>
        <!-- color picker for color-type properties -->
        <template v-if="selectedPropType === 'color'">
            <div class="flex items-center gap-2">
                <input type="color"
                    class="color-swatch"
                    :value="colorHex"
                    @input="update('value', $event.target.value)" />
                <input type="text"
                    :value="props.data.value ?? ''"
                    @input="update('value', $event.target.value)"
                    placeholder="#RRGGBB"
                    class="flex-1 font-mono" />
            </div>
        </template>
        <!-- boolean toggle -->
        <template v-else-if="selectedPropType === 'boolean'">
            <select :value="props.data.value ?? '1'" @change="update('value', $event.target.value)">
                <option value="1">true (1)</option>
                <option value="0">false (0)</option>
            </select>
        </template>
        <!-- default text/number input -->
        <template v-else>
            <div v-if="selectedPropType === 'text'" class="flex items-center gap-1">
                <input type="text"
                    :value="props.data.value ?? ''"
                    @input="update('value', $event.target.value)"
                    placeholder="value"
                    class="flex-1 min-w-0" />
                <button class="flex-shrink-0 px-1.5 py-0.5 rounded text-[10px] bg-white/10 hover:bg-white/20 text-gray-300 border border-white/20 leading-tight"
                    title="String builder / icon picker"
                    @click="showPicker = true">⌨</button>
            </div>
            <input v-else
                :type="selectedPropType === 'number' ? 'number' : 'text'"
                :value="props.data.value ?? ''"
                @input="update('value', $event.target.value)"
                placeholder="value" />
        </template>

        <p v-if="preview" class="text-ui-active font-mono text-[10px] mt-1">{{ preview }}</p>
    </NodeBase>
    <Handle type="source" :position="Position.Right" id="output" />
    <IconPicker v-model="showPicker"
        :initialText="props.data.value ?? ''"
        :availableTabs="pickerTabs"
        @text-updated="val => update('value', val)" />
</template>

<script setup>
defineOptions({ inheritAttrs: false });
import { inject, computed, ref } from 'vue';
import { Handle, Position, useVueFlow } from '@vue-flow/core';
import NodeBase from './NodeBase.vue';
import IconPicker from '../../components/IconPicker.vue';

const props = defineProps({ id: String, data: Object });
const { updateNodeData } = useVueFlow();
const haspObjects = inject('haspObjects', null);
const showPicker = ref(false);

const update = (key, val) => updateNodeData(props.id, { ...props.data, [key]: val });

const pages = computed(() => haspObjects?.pages?.value ?? []);

const objects = computed(() => {
    const pid = props.data?.pageId;
    if (pid === '' || pid === undefined || !haspObjects) return [];
    return haspObjects.objectsOnPage(parseInt(pid));
});

// propertiesForObject now returns [{name, type}] objects
const properties = computed(() => {
    const pid = props.data?.pageId;
    const oid = props.data?.objectId;
    if (pid === '' || pid === undefined || oid === '' || oid === undefined || !haspObjects) return [];
    return haspObjects.propertiesForObject(parseInt(pid), parseInt(oid));
});

const selectedProp = computed(() =>
    properties.value.find(p => p.name === props.data?.property) ?? null
);
const selectedPropType = computed(() => selectedProp.value?.type ?? 'text');

const pickerTabs = computed(() =>
    props.data?.property === 'template' ? ['datetime'] : ['icons', 'variables']
);

// Ensure value is a valid #RRGGBB hex string for the color input
const colorHex = computed(() => {
    const v = (props.data?.value ?? '').trim();
    return /^#[0-9a-fA-F]{6}$/.test(v) ? v : '#000000';
});

const preview = computed(() => {
    const { pageId, objectId, property, value } = props.data ?? {};
    if (pageId === '' || pageId === undefined || objectId === undefined || !property) return '';
    return `p${pageId}b${objectId}.${property}=${value ?? ''}`;
});

function onPageChange(val) {
    updateNodeData(props.id, { ...props.data, pageId: val !== '' ? parseInt(val) : '', objectId: '', property: '', value: '' });
}

function onObjectChange(val) {
    updateNodeData(props.id, { ...props.data, objectId: val !== '' ? parseInt(val) : '', property: '', value: '' });
}

function onPropChange(val) {
    // Reset value when property type changes
    updateNodeData(props.id, { ...props.data, property: val, value: '' });
}
</script>
